import type { GetCaseRecordResponse } from '@compass/client/types/case_record';

export interface AssigneeUpdatePlan {
  assigneeUserId: string;
  caseIds: string[];
  count: number;
  /** Optional team to move the case(s) to (set when the assignee was picked from a team group). */
  teamId?: string;
}

/**
 * Helper function to check if a case can be assigned
 */
export const canCaseBeAssigned = (case_: GetCaseRecordResponse): boolean => {
  return case_.status_flags?.blocked_on_manual_task || false;
};

/**
 * Separates cases into assignable and non-assignable categories
 */
export const separateAssignableCases = (
  cases: GetCaseRecordResponse[],
  assigneeUpdatePlan: AssigneeUpdatePlan,
) => {
  const selectedCases = cases.filter(c => c.id && assigneeUpdatePlan.caseIds.includes(c.id));

  const assignableCases = selectedCases.filter(
    case_ => canCaseBeAssigned(case_) && case_.assigned !== assigneeUpdatePlan.assigneeUserId,
  );

  const nonAssignableCases = selectedCases.filter(
    case_ => !canCaseBeAssigned(case_) || case_.assigned === assigneeUpdatePlan.assigneeUserId,
  );

  return { assignableCases, nonAssignableCases };
};

/**
 * Groups assignable cases by their current assignee
 */
export const groupCasesByCurrentAssignee = (
  assignableCases: GetCaseRecordResponse[],
): Record<string, GetCaseRecordResponse[]> => {
  return assignableCases.reduce(
    (groups, case_) => {
      const currentAssignee = case_.assigned || 'Unassigned';
      if (!groups[currentAssignee]) {
        groups[currentAssignee] = [];
      }
      groups[currentAssignee].push(case_);
      return groups;
    },
    {} as Record<string, GetCaseRecordResponse[]>,
  );
};

/**
 * Categorizes non-assignable cases by reason
 */
export const categorizeNonAssignableCases = (
  nonAssignableCases: GetCaseRecordResponse[],
  assigneeUserId: string,
) => {
  const wrongStateCases = nonAssignableCases.filter(case_ => !canCaseBeAssigned(case_));

  const alreadyAssignedCases = nonAssignableCases.filter(
    case_ => case_.assigned === assigneeUserId,
  );

  return { wrongStateCases, alreadyAssignedCases };
};

/**
 * Generates description text for wrong state cases
 */
export const getWrongStateCasesDescription = (wrongStateCases: GetCaseRecordResponse[]): string => {
  const uniqueStates = [...new Set(wrongStateCases.map(case_ => case_.state || 'unknown'))];
  const stateList = uniqueStates.join(', ');

  return `${wrongStateCases.length} case${wrongStateCases.length === 1 ? '' : 's'} in ${uniqueStates.length === 1 ? 'state' : 'states'} (${stateList}) do not allow assignee changes.`;
};
